import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import LeaderboardPodium from "@/components/leaderboard-podium";
import { useAuth } from "@/hooks/use-auth";
import { Trophy, User, MessageSquare, Users, Gift, Headset } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { User as UserType } from "@shared/schema";
import { Skeleton } from "@/components/ui/skeleton";
import { useState } from "react";

const fetchLeaderboard = async (): Promise<UserType[]> => {
    const res = await fetch("/api/leaderboard");
    if (!res.ok) {
      throw new Error("Failed to fetch leaderboard");
    }
    return res.json();
  };

const fetchCommunityStats = async (): Promise<any> => {
    const res = await fetch("/api/community-stats");
    if (!res.ok) {
        throw new Error("Failed to fetch community stats");
    }
    return res.json();
};

const Leaderboard = () => {
  const { user } = useAuth();
  const [visibleCount, setVisibleCount] = useState(5);

  const { data: leaderboardData = [], isLoading } = useQuery<UserType[]>({ 
    queryKey: ["leaderboard"],
    queryFn: fetchLeaderboard,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: communityStats, isLoading: isLoadingStats } = useQuery({ 
    queryKey: ["communityStats"],
    queryFn: fetchCommunityStats,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const currentUserPosition = user ? {
    rank: leaderboardData.findIndex(u => u.id === user.id) + 1,
    ...user,
    totalEarned: user?.totalEarned || 0,
  } : null;

  const topThree = leaderboardData.slice(0, 3);
  const otherUsers = leaderboardData.slice(3);
  const visibleOtherUsers = otherUsers.slice(0, visibleCount);

  const handleLoadMore = () => {
    setVisibleCount(prevCount => prevCount + 10);
  };

  return (
    <div className="py-20 px-4 bg-card/20">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold mb-4 flex items-center justify-center">
            <Trophy className="w-8 h-8 mr-3 text-primary"/>
            Leaderboard
          </h1>
          <p className="text-xl text-muted-foreground">
            See how you rank against other BloxBoost users
          </p>
        </motion.div>

        {/* Top 3 Podium */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {isLoading ? (
            <div className="flex justify-center items-end space-x-4 h-64">
                <Skeleton className="w-32 h-48 bg-muted/50" />
                <Skeleton className="w-32 h-64 bg-muted/50" />
                <Skeleton className="w-32 h-40 bg-muted/50" />
            </div>
          ) : (
            <LeaderboardPodium topUsers={topThree} />
          )}
        </motion.div>

        {/* Full Leaderboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Card className="glass-effect border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <Trophy className="w-5 h-5" />
                  <span>Full Rankings</span>
                </CardTitle>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-accent animate-pulse"></div>
                  <span>Live</span>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              <div className="space-y-2">
              {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="flex items-center space-x-4 p-4 rounded-lg bg-background/30">
                            <Skeleton className="h-10 w-10 rounded-full bg-muted/50" />
                            <div className="flex-1 space-y-2">
                                <Skeleton className="h-4 w-3/4 bg-muted/50" />
                                <Skeleton className="h-4 w-1/2 bg-muted/50" />
                            </div>
                        </div>
                    ))
                ) : (
                  <>
                    {visibleOtherUsers.map((leaderUser, index) => (
                      <motion.div
                        key={leaderUser.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * index, duration: 0.4 }}
                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 rounded-lg bg-background/30 border border-border hover:bg-background/50 transition-colors duration-300"
                        data-testid={`leaderboard-rank-${index + 4}`}
                      >
                        <div className="flex items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
                          <div className="w-8 h-8 rounded-full bg-muted flex-shrink-0 flex items-center justify-center">
                            <span className="text-sm font-bold">{index + 4}</span>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex-shrink-0 flex items-center justify-center">
                            {leaderUser.avatarUrl ? (
                              <img
                                src={leaderUser.avatarUrl}
                                alt="Avatar"
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <User className="w-5 h-5 text-white" />
                            )}
                          </div>
                          <div className="flex-grow min-w-0">
                            <div className="font-medium truncate" data-testid={`user-${leaderUser.id}-name`}>
                              {leaderUser.displayName}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              @{leaderUser.username}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 sm:space-x-6 mt-3 sm:mt-0 self-end sm:self-auto">
                          <div className="text-right w-24 text-sm">
                            <div className="font-bold text-primary" data-testid={`user-${leaderUser.id}-total`}>
                              {leaderUser.totalEarned.toLocaleString()}
                            </div>
                            <div className="text-xs text-muted-foreground">tokens</div>
                          </div>
                        </div>
                      </motion.div>
                    ))}

                    {/* Current User Position */}
                    {currentUserPosition && currentUserPosition.rank > 0 && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6, duration: 0.4 }}
                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 rounded-lg bg-primary/10 border-2 border-primary/20 hover:bg-primary/20 transition-colors duration-300 mt-4"
                        data-testid="current-user-rank"
                      >
                        <div className="flex items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
                          <div className="w-8 h-8 rounded-full bg-primary flex-shrink-0 flex items-center justify-center">
                            <span className="text-sm font-bold text-primary-foreground">
                              {currentUserPosition.rank}
                            </span>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex-shrink-0 flex items-center justify-center">
                            {currentUserPosition.avatarUrl ? (
                              <img
                                src={currentUserPosition.avatarUrl}
                                alt="Avatar"
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <User className="w-5 h-5 text-white" />
                            )}
                          </div>
                          <div className="flex-grow min-w-0">
                            <div className="font-medium text-primary truncate">
                              {currentUserPosition.displayName || currentUserPosition.username} (You)
                            </div>
                            <div className="text-sm text-primary/80">
                              @{currentUserPosition.username}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 sm:space-x-6 mt-3 sm:mt-0 self-end sm:self-auto">
                          <div className="text-right w-24 text-sm">
                            <div className="font-bold text-primary" data-testid="current-user-total">
                              {currentUserPosition.totalEarned.toLocaleString()}
                            </div>
                            <div className="text-xs text-primary/80">tokens</div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </>
                )}
              </div>
              {otherUsers.length > visibleCount && (
              <div className="mt-6 text-center">
                <Button variant="ghost" data-testid="button-load-more" onClick={handleLoadMore}>
                  Load More Rankings
                </Button>
              </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Discord Community Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12"
        >
          <Card className="glass-effect border-border text-center">
            <CardContent className="p-6">
              <motion.div
                className="w-14 h-14 rounded-full bg-primary/10 border border-primary/20 text-primary flex items-center justify-center mx-auto mb-4"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <MessageSquare className="w-7 h-7" />
              </motion.div>

              <h2 className="text-2xl font-bold mb-3">Join Our Discord Community</h2>
              <p className="text-lg text-muted-foreground mb-6 max-w-xl mx-auto">
                Connect with other users, get exclusive promo codes, and stay updated with the latest features and offers.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <motion.div className="p-4 rounded-lg bg-background/30 border border-border flex flex-col items-center justify-center text-center hover-lift" whileHover={{ scale: 1.05 }}>
                  <Users className="w-7 h-7 mb-2 text-primary" />
                  <div className="text-xl font-bold text-primary">{isLoadingStats ? <Skeleton className="h-7 w-20 mx-auto" /> : communityStats?.members.toLocaleString() ?? '5,137'}</div>
                  <div className="text-xs text-muted-foreground">Members</div>
                </motion.div>
                <motion.div className="p-4 rounded-lg bg-background/30 border border-border flex flex-col items-center justify-center text-center hover-lift" whileHover={{ scale: 1.05 }}>
                  <Gift className="w-7 h-7 mb-2 text-secondary" />
                  <div className="text-xl font-bold text-secondary">{isLoadingStats ? <Skeleton className="h-7 w-12 mx-auto" /> : communityStats?.dailyCodes ?? '5'}</div>
                  <div className="text-xs text-muted-foreground">Daily Codes</div>
                </motion.div>
                <motion.div className="p-4 rounded-lg bg-background/30 border border-border flex flex-col items-center justify-center text-center hover-lift" whileHover={{ scale: 1.05 }}>
                  <Headset className="w-7 h-7 mb-2 text-accent" />
                  <div className="text-xl font-bold text-accent">{isLoadingStats ? <Skeleton className="h-7 w-16 mx-auto" /> : communityStats?.support ?? '24/7'}</div>
                  <div className="text-xs text-muted-foreground">Support</div>
                </motion.div>
              </div>

              <Button 
                className="px-6 py-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold glow-effect shadow-primary/20" 
                data-testid="button-join-discord"
                onClick={() => window.open('https://discord.gg/erHNtCTkpa', '_blank')}>
                Join Discord Server
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Leaderboard;
